import styled from '@emotion/styled';
import { Button, Form, Input, Space, Typography } from 'antd';
import App from 'antd/lib/app';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { API, TOKEN_LS_PATH } from '../api';
import { TUseFormEnterProps, useFormEnterPress } from '../hooks/forms';
import { LocaleKeys } from '../locale';
import { useAppDispatch } from '../state';
import { loginAction } from '../state/auth/slice';
import { meActions } from '../state/me/slice';
import { playlistCurrentActions } from '../state/playlists/currentSlice';
import { playListAllActions } from '../state/playlists/listSlice';
import { Paths } from '../types/common';
import { TLoginForm } from '../types/users';

const Title = styled(Typography.Title)`
  color: ${({ theme }) => theme.COLORS.WHITE._100} !important;
  margin: 0;
  margin-bottom: 16px !important;
`;

const StyledSpace = styled(Space)`
  width: 100%;

  .ant-space-item {
    flex: 1;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledNavLink = styled(NavLink)`
  flex: 1;
`;

export const Login: FC = () => {
  const { t } = useTranslation([LocaleKeys.LOGIN]);
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const [form] = Form.useForm<TLoginForm>();
  const [loading, setLoading] = useState(false);

  const onFormSubmit = useCallback(
    async (values: TLoginForm) => {
      setLoading(true);

      try {
        console.log('[Cabinet Login Page] Submitting credentials:', { email: values.email });
        const response = await API.auth.login(values);
        console.log('[Cabinet Login Page] Response received:', response);

        if (response.data.authorizationToken) {
          console.log('[Cabinet Login Page] Login success, dispatching action');
          message.success(t('login.success'));
          setLoading(false);

          localStorage.setItem(TOKEN_LS_PATH, response.data.authorizationToken);

          dispatch(meActions.resetMe());
          dispatch(playlistCurrentActions.resetState());
          dispatch(playListAllActions.resetState());
          dispatch(loginAction(response.data.authorizationToken));
        } else {
          console.warn('[Cabinet Login Page] No authorizationToken in response data');
          message.error(t('login.error'));
          setLoading(false);
        }
      } catch (e) {
        console.error('[Cabinet Login Page] Caught login error:', e);
        message.error(t('login.error'));
        setLoading(false);
      }
    },
    [t]
  );

  const formEnterProps: TUseFormEnterProps<typeof form.submit> = useMemo(
    () => ({
      submitFunc: form.submit,
    }),
    []
  );

  useFormEnterPress(formEnterProps);

  return (
    <div>
      <Title level={4}>{t('login.title')}</Title>
      <Form
        layout="vertical"
        autoComplete="off"
        requiredMark={false}
        form={form}
        onFinish={onFormSubmit}
      >
        <Form.Item
          name="email"
          label="Email"
          className="themed"
          rules={[
            {
              required: true,
              message: t('rules.required'),
            },
          ]}
        >
          <Input
            className="themed"
            style={{
              width: 330,
            }}
          />
        </Form.Item>
        <Form.Item
          name="password"
          label={t('login.password')}
          className="themed"
          rules={[
            {
              required: true,
              message: t('rules.required'),
            },
          ]}
        >
          <Input.Password
            className="themed"
            style={{
              width: 330,
            }}
            autoComplete="off"
          />
        </Form.Item>
      </Form>
      <StyledSpace>
        <StyledButton type="primary" onClick={form.submit} loading={loading}>
          {t('buttons.account.enter')}
        </StyledButton>
        <StyledNavLink to={Paths.FORGOT_PASSWORD}>
          <Button type="text" className="themed">
            {t('buttons.forgot')}
          </Button>
        </StyledNavLink>
        <StyledNavLink to={Paths.REGISTRATION}>
          <Button type="text" className="themed">
            {t('buttons.account.no')}
          </Button>
        </StyledNavLink>
      </StyledSpace>
    </div>
  );
};
